import { useEffect, useRef, useState } from 'react';
import './style.scss';
import { ISurveyModalFormSettings, ISurveyModalFormTexts } from './survey-modal-form.interface';
import { ISurveyModalFormApiModel } from './models/survey-modal-form.api-model';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';
import FocusTrap from '../../Shared/FocusTrap';
import RadioFormGroup from '../../Shared/RadioFormGroup';
import TextFieldFormGroup, { ITextFieldFormGroupState } from '../../Shared/TextFieldFormGroup';
import HoneyPot, { IHoneyPot } from '../../Shared/HoneyPot';
import ValidationInfo from '../../../validation/validation-info';
import { useTexts } from '../../../hooks/texts.hook';
import { LanguageService } from '../../../services/language.service';
import AntiforgeryService from '../../../services/anti-forgery.service';
import LogService from '../../../services/log-service/log.service';
import { SanitizerService } from '../../../services/sanitizer.service';
import { AnalyticsService } from '../../../services/analytics-service';
import { ClientSideStorageItems, ClientSideStorageService } from '../../../services/client-side-storage.service';
import ContainerItemBlockHeading from '../../Shared/ContainerItemBlockHeading';

type ISurveyModalFormProps = IServerSideRendered<ISurveyModalFormTexts, ISurveyModalFormSettings>;

type SurveyAnswers = Partial<Omit<ISurveyModalFormApiModel, '$type' | 'blockContentGuid' | 'pageContentGuid'>>;

const COMPLETED = 'completed';
const DISMISSED = 'dismissed';

async function shouldShowFromServer(blockContentGuid: string): Promise<boolean> {
  return await fetch('/api/survey-modal-form/status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
    body: JSON.stringify({ blockContentGuid }),
  })
    .then((response) => (response.ok ? response.json() : false))
    .catch(() => false);
}

function getSurveyStorageItem(blockContentGuid: string) {
  return { ...ClientSideStorageItems.LocalStorage.SurveyModal, key: `${ClientSideStorageItems.LocalStorage.SurveyModal.key}-${blockContentGuid}` };
}

function shouldHideFromLocalStorage(blockContentGuid: string): boolean {
  try {
    const value = ClientSideStorageService.getLocalStorage(getSurveyStorageItem(blockContentGuid));
    return value === COMPLETED || value === DISMISSED;
  } catch {
    return false;
  }
}

function markCompleted(blockContentGuid: string): void {
  try {
    ClientSideStorageService.setLocalStorage(getSurveyStorageItem(blockContentGuid), COMPLETED);
  } catch {
    // ignore
  }
}

function markDismissed(blockContentGuid: string): void {
  try {
    ClientSideStorageService.setLocalStorage(getSurveyStorageItem(blockContentGuid), DISMISSED);
  } catch {
    // ignore
  }
}

function buildOptions(optionTexts: string[]): { value: string; text: string }[] {
  return optionTexts.flatMap((text, index) => {
    const trimmed = text.trim();
    return trimmed ? [{ value: String(index + 1), text: trimmed }] : [];
  });
}

enum StepType {
  Intro = 'intro',
  Radio = 'radio',
}

interface IntroStep {
  type: StepType.Intro;
  html: string;
}

interface RadioStep {
  type: StepType.Radio;
  question: string;
  options: { value: string; text: string }[];
  answerKey: keyof SurveyAnswers;
  commentAnswerOption: string;
  commentRequired: boolean;
  commentLabelText: string;
  commentKey: keyof SurveyAnswers;
  skipAnswerOption: string;
  skipSteps: number;
}

type SurveyStep = IntroStep | RadioStep;

function buildSteps(texts: ISurveyModalFormTexts, settings: ISurveyModalFormSettings): SurveyStep[] {
  if (!texts.questionOneQuestion.trim() || !texts.questionOneOptionOne.trim()) return [];

  const steps: SurveyStep[] = [];
  if (texts.introductionHtml) steps.push({ type: StepType.Intro, html: texts.introductionHtml });
  steps.push({
    type: StepType.Radio,
    question: texts.questionOneQuestion,
    options: buildOptions([
      texts.questionOneOptionOne,
      texts.questionOneOptionTwo,
      texts.questionOneOptionThree,
      texts.questionOneOptionFour,
      texts.questionOneOptionFive,
      texts.questionOneOptionSix,
      texts.questionOneOptionSeven,
      texts.questionOneOptionEight,
      texts.questionOneOptionNine,
      texts.questionOneOptionTen,
    ]),
    answerKey: 'questionOneAnswer',
    commentAnswerOption: settings.questionOneCommentAnswerOption,
    commentRequired: settings.questionOneCommentRequired,
    commentLabelText: texts.questionOneCommentLabelText,
    commentKey: 'questionOneComment',
    skipAnswerOption: settings.questionOneSkipAnswerOption,
    skipSteps: settings.questionOneSkipSteps,
  });
  if (texts.questionTwoQuestion && texts.questionTwoOptionOne.trim())
    steps.push({
      type: StepType.Radio,
      question: texts.questionTwoQuestion,
      options: buildOptions([
        texts.questionTwoOptionOne,
        texts.questionTwoOptionTwo,
        texts.questionTwoOptionThree,
        texts.questionTwoOptionFour,
        texts.questionTwoOptionFive,
        texts.questionTwoOptionSix,
        texts.questionTwoOptionSeven,
        texts.questionTwoOptionEight,
        texts.questionTwoOptionNine,
        texts.questionTwoOptionTen,
      ]),
      answerKey: 'questionTwoAnswer',
      commentAnswerOption: settings.questionTwoCommentAnswerOption,
      commentRequired: settings.questionTwoCommentRequired,
      commentLabelText: texts.questionTwoCommentLabelText,
      commentKey: 'questionTwoComment',
      skipAnswerOption: settings.questionTwoSkipAnswerOption,
      skipSteps: settings.questionTwoSkipSteps,
    });
  if (texts.questionThreeQuestion && texts.questionThreeOptionOne.trim())
    steps.push({
      type: StepType.Radio,
      question: texts.questionThreeQuestion,
      options: buildOptions([
        texts.questionThreeOptionOne,
        texts.questionThreeOptionTwo,
        texts.questionThreeOptionThree,
        texts.questionThreeOptionFour,
        texts.questionThreeOptionFive,
        texts.questionThreeOptionSix,
        texts.questionThreeOptionSeven,
        texts.questionThreeOptionEight,
        texts.questionThreeOptionNine,
        texts.questionThreeOptionTen,
      ]),
      answerKey: 'questionThreeAnswer',
      commentAnswerOption: settings.questionThreeCommentAnswerOption,
      commentRequired: settings.questionThreeCommentRequired,
      commentLabelText: texts.questionThreeCommentLabelText,
      commentKey: 'questionThreeComment',
      skipAnswerOption: settings.questionThreeSkipAnswerOption,
      skipSteps: settings.questionThreeSkipSteps,
    });
  if (texts.questionFourQuestion && texts.questionFourOptionOne.trim())
    steps.push({
      type: StepType.Radio,
      question: texts.questionFourQuestion,
      options: buildOptions([
        texts.questionFourOptionOne,
        texts.questionFourOptionTwo,
        texts.questionFourOptionThree,
        texts.questionFourOptionFour,
        texts.questionFourOptionFive,
        texts.questionFourOptionSix,
        texts.questionFourOptionSeven,
        texts.questionFourOptionEight,
        texts.questionFourOptionNine,
        texts.questionFourOptionTen,
      ]),
      answerKey: 'questionFourAnswer',
      commentAnswerOption: settings.questionFourCommentAnswerOption,
      commentRequired: settings.questionFourCommentRequired,
      commentLabelText: texts.questionFourCommentLabelText,
      commentKey: 'questionFourComment',
      skipAnswerOption: settings.questionFourSkipAnswerOption,
      skipSteps: settings.questionFourSkipSteps,
    });
  if (texts.questionFiveQuestion && texts.questionFiveOptionOne.trim())
    steps.push({
      type: StepType.Radio,
      question: texts.questionFiveQuestion,
      options: buildOptions([
        texts.questionFiveOptionOne,
        texts.questionFiveOptionTwo,
        texts.questionFiveOptionThree,
        texts.questionFiveOptionFour,
        texts.questionFiveOptionFive,
        texts.questionFiveOptionSix,
        texts.questionFiveOptionSeven,
        texts.questionFiveOptionEight,
        texts.questionFiveOptionNine,
        texts.questionFiveOptionTen,
      ]),
      answerKey: 'questionFiveAnswer',
      commentAnswerOption: settings.questionFiveCommentAnswerOption,
      commentRequired: settings.questionFiveCommentRequired,
      commentLabelText: texts.questionFiveCommentLabelText,
      commentKey: 'questionFiveComment',
      skipAnswerOption: settings.questionFiveSkipAnswerOption,
      skipSteps: settings.questionFiveSkipSteps,
    });
  if (texts.questionSixQuestion && texts.questionSixOptionOne.trim())
    steps.push({
      type: StepType.Radio,
      question: texts.questionSixQuestion,
      options: buildOptions([
        texts.questionSixOptionOne,
        texts.questionSixOptionTwo,
        texts.questionSixOptionThree,
        texts.questionSixOptionFour,
        texts.questionSixOptionFive,
        texts.questionSixOptionSix,
        texts.questionSixOptionSeven,
        texts.questionSixOptionEight,
        texts.questionSixOptionNine,
        texts.questionSixOptionTen,
      ]),
      answerKey: 'questionSixAnswer',
      commentAnswerOption: settings.questionSixCommentAnswerOption,
      commentRequired: settings.questionSixCommentRequired,
      commentLabelText: texts.questionSixCommentLabelText,
      commentKey: 'questionSixComment',
      skipAnswerOption: settings.questionSixSkipAnswerOption,
      skipSteps: settings.questionSixSkipSteps,
    });
  if (texts.questionSevenQuestion && texts.questionSevenOptionOne.trim())
    steps.push({
      type: StepType.Radio,
      question: texts.questionSevenQuestion,
      options: buildOptions([
        texts.questionSevenOptionOne,
        texts.questionSevenOptionTwo,
        texts.questionSevenOptionThree,
        texts.questionSevenOptionFour,
        texts.questionSevenOptionFive,
        texts.questionSevenOptionSix,
        texts.questionSevenOptionSeven,
        texts.questionSevenOptionEight,
        texts.questionSevenOptionNine,
        texts.questionSevenOptionTen,
      ]),
      answerKey: 'questionSevenAnswer',
      commentAnswerOption: settings.questionSevenCommentAnswerOption,
      commentRequired: settings.questionSevenCommentRequired,
      commentLabelText: texts.questionSevenCommentLabelText,
      commentKey: 'questionSevenComment',
      skipAnswerOption: '',
      skipSteps: 0,
    });
  return steps;
}

function SurveyModalForm({ settings, texts }: ISurveyModalFormProps) {
  // === HOOKS ===
  const { t } = useTexts();
  const honeyPotRef = useRef<IHoneyPot>(null!);
  const isSubmittingRef = useRef(false);
  const stepAnnouncerRef = useRef<HTMLSpanElement>(null);
  const thankYouRef = useRef<HTMLDivElement>(null);
  const isInitialStepRender = useRef(true);

  // === CONFIGURATION ===
  const steps = buildSteps(texts, settings);
  const headingElement = (
    <ContainerItemBlockHeading headingText={texts.heading} headingLevel={settings.headingLevel} headingLevelClass={settings.headingLevelClass} headingVisuallyHidden={settings.headingVisuallyHidden} />
  );

  // === STATE ===
  const [show, setShow] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [selectedOption, setSelectedOption] = useState('');
  const [commentState, setCommentState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showError, setShowError] = useState(false);

  // === EFFECTS ===
  useEffect(() => {
    let isActive = true;

    async function updateShow() {
      const canShow = ClientSideStorageService.isConsentStatusSet() && !shouldHideFromLocalStorage(settings.blockContentGuid);
      const shouldShow = canShow && (await shouldShowFromServer(settings.blockContentGuid));
      if (isActive) setShow(shouldShow);
    }

    function handleConsentApproved() {
      void updateShow();
    }

    document.addEventListener('consent-status-approved', handleConsentApproved);
    void updateShow();
    return () => {
      isActive = false;
      document.removeEventListener('consent-status-approved', handleConsentApproved);
    };
  }, [settings.blockContentGuid]);

  useEffect(() => {
    if (isInitialStepRender.current) {
      isInitialStepRender.current = false;
      return;
    }
    stepAnnouncerRef.current?.focus();
  }, [currentStepIndex]);

  useEffect(() => {
    if (!isSubmitted) return;
    thankYouRef.current?.focus();
  }, [isSubmitted]);

  if (!show || steps.length === 0) return null;

  // === VARIABLES ===
  const step = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const questionCount = steps.filter((s) => s.type !== StepType.Intro).length;
  const currentQuestionNumber = steps.slice(0, currentStepIndex + 1).filter((s) => s.type !== StepType.Intro).length;
  const showComment =
    step.type === StepType.Radio &&
    !!step.commentAnswerOption &&
    step.commentAnswerOption
      .split(',')
      .map((s) => s.trim())
      .includes(selectedOption);
  const canProceed = step.type === StepType.Intro ? true : !!selectedOption && (!showComment || !step.commentRequired || !!commentState.value.trim());

  // === HANDLERS ===
  function handleDismiss() {
    if (!isSubmitted) markDismissed(settings.blockContentGuid);
    setShow(false);
  }

  async function handleNext() {
    if (!canProceed || isSubmittingRef.current || !honeyPotRef.current.isValid()) return;

    const updatedAnswers: SurveyAnswers = { ...answers };
    if (step.type === StepType.Radio) {
      (updatedAnswers as Record<string, unknown>)[step.answerKey] = Number(selectedOption);
      if (showComment) (updatedAnswers as Record<string, unknown>)[step.commentKey] = commentState.value.trim();
    }

    let stepsToAdvance = 1;
    if (step.type === StepType.Radio && step.skipSteps > 0) {
      const matchesSkip = step.skipAnswerOption
        .split(',')
        .map((s) => s.trim())
        .includes(selectedOption);
      if (matchesSkip) stepsToAdvance = 1 + step.skipSteps;
    }
    const nextStepIndex = currentStepIndex + stepsToAdvance;

    if (nextStepIndex < steps.length) {
      setAnswers(updatedAnswers);
      setCurrentStepIndex(nextStepIndex);
      setSelectedOption('');
      setCommentState({ value: '', validationInfo: new ValidationInfo() });
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setShowError(false);
    try {
      const requestData: ISurveyModalFormApiModel = {
        $type: 'SurveyModal',
        blockContentGuid: settings.blockContentGuid,
        pageContentGuid: settings.pageContentGuid,
        questionOneAnswer: (updatedAnswers as ISurveyModalFormApiModel).questionOneAnswer!,
        ...updatedAnswers,
      };
      const formData = new FormData();
      formData.append('feedbackForm', JSON.stringify(requestData));

      const response = await fetch(`/api/forms/submit?language=${LanguageService.getCurrentLanguage()}`, {
        method: 'POST',
        headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to submit survey');

      markCompleted(settings.blockContentGuid);
      setIsSubmitted(true);
    } catch (error) {
      LogService.logError(error);
      AnalyticsService.addEvent(AnalyticsService.Forms, AnalyticsService.ErrorAction, 'Surveyform modal - submit error');
      setShowError(true);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <FocusTrap isVisible onEscape={handleDismiss}>
        <div className="modal d-block fade show survey-modal-form" aria-labelledby="survey-modal-form-title" aria-modal="true" role="dialog">
          <div className="modal-dialog modal-dialog-centered survey-modal-form__modal-dialog">
            <div className="modal-content">
              <div className="modal-header pb-0 d-flex flex-column">
                <div className="d-flex justify-content-between w-100">
                  <div id="survey-modal-form-title">{headingElement}</div>
                  <div className="d-none d-md-flex align-items-center gap-3">
                    {!isSubmitted && step.type !== StepType.Intro && (
                      <span className="text-muted small">
                        {t('Steg', 'Step')} {currentQuestionNumber} {t('av', 'of')} {questionCount}
                      </span>
                    )}
                    <button type="button" className="btn-close" aria-label={t('Stäng', 'Close')} onClick={handleDismiss} />
                  </div>
                  <button type="button" className="btn-close d-md-none" aria-label={t('Stäng', 'Close')} onClick={handleDismiss} />
                </div>
                <div className="d-flex justify-content-start w-100 d-md-none">
                  {!isSubmitted && step.type !== StepType.Intro && (
                    <p className="text-muted small mb-0">
                      {t('Steg', 'Step')} {currentQuestionNumber} {t('av', 'of')} {questionCount}
                    </p>
                  )}
                </div>
              </div>
              <form
                className="d-flex flex-column flex-grow-1"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  handleNext();
                }}
              >
                <div key={currentStepIndex} className="modal-body pb-0">
                  {isSubmitted ? (
                    <>
                      <div
                        ref={thankYouRef}
                        tabIndex={-1}
                        className="last-child-margin-bottom-none"
                        dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.thankYouHtml || texts.submitted) }}
                      />
                    </>
                  ) : (
                    <>
                      {step.type === StepType.Radio && (
                        <span ref={stepAnnouncerRef} tabIndex={-1} className="visually-hidden">
                          {t('Steg', 'Step')} {currentQuestionNumber} {t('av', 'of')} {questionCount}
                        </span>
                      )}
                      {step.type === StepType.Intro && <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(step.html) }} />}
                      {step.type === StepType.Radio && (
                        <RadioFormGroup
                          options={step.options}
                          rowLayout={false}
                          labelText={step.question}
                          state={{ value: selectedOption, validationInfo: new ValidationInfo() }}
                          onChange={(val) => {
                            setSelectedOption(val);
                            setCommentState({ value: '', validationInfo: new ValidationInfo() });
                          }}
                        />
                      )}
                      {step.type === StepType.Radio && showComment && (
                        <div className="mt-2">
                          <TextFieldFormGroup
                            className="mb-0"
                            labelText={step.commentLabelText}
                            required={step.commentRequired}
                            inputId="survey-modal-comment"
                            serverValidationName="comment"
                            autocomplete="off"
                            renderAsTextArea
                            rows={2}
                            maxLength={500}
                            state={commentState}
                            onChange={(val) => setCommentState({ ...commentState, value: val })}
                          />
                        </div>
                      )}
                      {showError && (
                        <p className="text-danger mt-3" role="alert">
                          {t('Något gick fel. Försök igen.', 'Something went wrong. Please try again.')}
                        </p>
                      )}
                      <HoneyPot ref={honeyPotRef} />
                    </>
                  )}
                </div>
                {isSubmitted && (
                  <div className="modal-footer mt-auto">
                    <button type="button" className="btn btn-primary w-100" onClick={() => setShow(false)}>
                      {t('Stäng', 'Close')}
                    </button>
                  </div>
                )}
                {!isSubmitted && (
                  <div className="modal-footer mt-auto">
                    <button type="submit" className="btn btn-primary w-100" disabled={!canProceed || isSubmitting}>
                      {isLastStep ? texts.submit : t('Fortsätt', 'Continue')}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </FocusTrap>
      <div className="modal-backdrop fade show" />
    </>
  );
}

export default SurveyModalForm;

